import type { QueryClient } from '@tanstack/react-query'

import { apiClient, getData, getPage } from '@/api/client'
import type { components } from '@/api/schema'
import { marketEndpoints } from '@/api/endpointCache'
import { LIVE_MAX_LIMIT } from '@/features/market/api/marketApi'
import { sparklineValues } from '@/features/market/api/marketMappers'
import {
  popularStocksSchema,
  stockCandidateSchema,
  watchRowSchema,
  watchlistCollectionSchema,
  type PopularStocks,
  type StockCandidate,
  type WatchlistCollection,
  type WatchRow,
} from '@/schemas/watchlist'

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

const listGroups = () => getPage<Wire<'WatchlistGroup'>>(() => apiClient.GET('/api/v5/watchlists/'))

/** The endpoint's ceiling. Asking for more is a 400, not a bigger page. */
const STOCKS_PAGE_SIZE = 200

/**
 * How many of `/market/live/`'s rows the picker's idle "Popular now" list keeps: the 200
 * most-traded, as it always was. The read asks for `LIVE_MAX_LIMIT` all the same, so it is the
 * same endpoint-cache entry Live market, Floorsheet and Depth already hold and costs no request of
 * its own, but a list of every company is not a "popular" one.
 */
const POPULAR_CANDIDATES = 200

/**
 * `/market/search/`'s own ceiling, and it is lower than every other list's.
 *
 * `MarketSearchRequestSerializer` validates `limit` at `max_value=50`
 * (mm-be api/v5/market/serializers.py), and the service clamps to the same
 * `MAX_LIMIT = 50` (services/market/search.py). Asking for 200 here is a 400,
 * not two hundred rows — which is worth stating, because the 200 that appears
 * beside every other market call is exactly the wrong number for this one.
 */
const SEARCH_LIMIT = 50

/** `q` is `max_length=100` server-side, so a longer box is trimmed, not 400'd. */
const SEARCH_MAX_LENGTH = 100

/**
 * `company_type`, in wire spelling — `apps/market/models.py` names three of
 * them (`INDEX`, `SUB_INDEX`, `STOCK`) and `services/public_issues/repository.py`
 * a fourth (`MUTUAL_FUND`).
 *
 * The picker EXCLUDES the two index types rather than keeping only `STOCK`,
 * which is the same shape as mm-be's own `indices=False` mode
 * (`repository.search_companies` excludes `_INDEX_TYPES`). Keeping only
 * `STOCK` would quietly drop listed mutual funds, and a listed fund is a real
 * thing to follow.
 */
const INDEX_TYPES: ReadonlySet<string> = new Set(['INDEX', 'SUB_INDEX'])

const listStocks = (groupId: number, pageSize: number, page = 1) =>
  getPage<Wire<'WatchStock'>>(() =>
    apiClient.GET('/api/v5/watchlists/{group_id}/stocks/', {
      params: { path: { group_id: groupId }, query: { page, page_size: pageSize } },
    }),
  )

/**
 * Every member of a list, not the first page of one.
 *
 * mm-be started paging this endpoint at 50 on 2026-09-15, and the default page
 * is silent: a member with sixty names saw fifty and no pager. Two callers here
 * need the complete set — `collection` draws it, and `setSymbols` diffs against
 * it, where a partial `current` is worse than a short table because a name it
 * could not see reads as "not in the list yet" and gets re-added.
 *
 * `stockCount` is the group's `stock_count` from `/watchlists/`, when the
 * caller has it: the page count is known before any page is asked for, so
 * every page goes out at once rather than page 1 first and the rest after it.
 * A list under 200 names is still exactly one request.
 *
 * The count is a hint, not the authority — `total_pages` on page 1 is. A name
 * added on another device between the two reads can push the list over a page
 * boundary, so any page the count did not foresee is fetched afterwards rather
 * than silently dropped. Without a count (`setSymbols`), page 1 goes first and
 * the rest together, as before.
 */
const listAllStocks = async (
  groupId: number,
  stockCount?: number,
): Promise<Wire<'WatchStock'>[]> => {
  const expected =
    stockCount === undefined ? 1 : Math.max(1, Math.ceil(stockCount / STOCKS_PAGE_SIZE))
  const known = await Promise.all(
    Array.from({ length: expected }, (_, index) =>
      listStocks(groupId, STOCKS_PAGE_SIZE, index + 1),
    ),
  )
  const total = known[0]?.meta?.total_pages ?? 1
  const late =
    total > expected
      ? await Promise.all(
          Array.from({ length: total - expected }, (_, index) =>
            listStocks(groupId, STOCKS_PAGE_SIZE, expected + index + 1),
          ),
        )
      : []
  // A list that shrank since the count leaves trailing pages empty — mm-be
  // pages by offset (`services/common/paging.py`), so a page past the end is
  // an empty page, not a 404.
  return [...known, ...late].flatMap((page) => page.rows)
}

const addStock = (groupId: number, symbol: string) =>
  getData<Wire<'WatchStock'>>(() =>
    apiClient.POST('/api/v5/watchlists/{group_id}/stocks/', {
      params: { path: { group_id: groupId } },
      body: { symbol },
    }),
  )

const removeStock = (groupId: number, symbol: string) =>
  getData<Wire<'RemovedResponse'>>(() =>
    apiClient.DELETE('/api/v5/watchlists/{group_id}/stocks/{symbol}/', {
      params: { path: { group_id: groupId, symbol } },
    }),
  )

// `WatchStock` carries no `logo_url` — StockAvatar's CDN logo, built from the
// symbol, covers a watchlist row instead.
const watchRow = (row: Wire<'WatchStock'>): WatchRow =>
  watchRowSchema.parse({
    symbol: row.key,
    name: row.name,
    ltp: row.ltp,
    percentChange: row.change_percent,
    sparkline: sparklineValues(row.sparkline),
    logo: null,
  })

// `LiveRow` carries `logo_url` straight from the wire; StockAvatar tries it
// after the CDN logo it builds from the symbol.
const liveRow = (row: Wire<'LiveRow'>): WatchRow =>
  watchRowSchema.parse({
    symbol: row.key,
    name: row.name,
    ltp: row.ltp,
    percentChange: row.change_percent,
    sparkline: sparklineValues(row.sparkline),
    logo: row.logo_url,
  })

const groupId = (id: string): number => {
  const parsed = Number(id)
  if (!Number.isInteger(parsed) || parsed < 1) throw new Error('Invalid watchlist')
  return parsed
}

export const watchlistService = {
  collection: async (client?: QueryClient): Promise<WatchlistCollection> => {
    const [groups, live] = await Promise.all([
      listGroups(),
      marketEndpoints(client).live(LIVE_MAX_LIMIT),
    ])
    // Groups first is a real dependency: the stocks endpoint is per group id.
    const stocks = await Promise.all(
      groups.rows.map((group) => listAllStocks(group.id, group.stock_count)),
    )
    return watchlistCollectionSchema.parse({
      lists: groups.rows.map((group, index) => ({
        id: String(group.id),
        name: group.name,
        rows: (stocks[index] ?? []).map(watchRow),
      })),
      candidates: live.rows.slice(0, POPULAR_CANDIDATES).map(liveRow),
    })
  },

  setSymbols: async (id: string, symbols: string[]): Promise<void> => {
    const idNumber = groupId(id)
    const current = await listAllStocks(idNumber)
    const currentSymbols = new Set(current.map((row) => row.key))
    const nextSymbols = new Set(symbols)
    await Promise.all([
      ...[...nextSymbols]
        .filter((symbol) => !currentSymbols.has(symbol))
        .map((symbol) => addStock(idNumber, symbol)),
      ...[...currentSymbols]
        .filter((symbol) => !nextSymbols.has(symbol))
        .map((symbol) => removeStock(idNumber, symbol)),
    ])
  },

  /**
   * One name onto one list — `POST /watchlists/{group_id}/stocks/`. Unlike `setSymbols`, it never
   * reads or rewrites the rest of the list, so two quick adds cannot race each other out.
   */
  addSymbol: async (id: string, symbol: string): Promise<void> => {
    await addStock(groupId(id), symbol)
  },

  /** One name off one list — `DELETE /watchlists/{group_id}/stocks/{symbol}/`. Home's rail Edit. */
  removeSymbol: async (id: string, symbol: string): Promise<void> => {
    await removeStock(groupId(id), symbol)
  },

  createList: async (name: string): Promise<string> => {
    const group = await getData<Wire<'WatchlistGroup'>>(() =>
      apiClient.POST('/api/v5/watchlists/', { body: { name: name.trim() } }),
    )
    return String(group.id)
  },

  renameList: async (id: string, name: string): Promise<void> => {
    await getData<Wire<'WatchlistGroup'>>(() =>
      apiClient.PATCH('/api/v5/watchlists/{group_id}/', {
        params: { path: { group_id: groupId(id) } },
        body: { name: name.trim() },
      }),
    )
  },

  deleteList: async (id: string): Promise<void> => {
    await getData<Wire<'DeletedResponse'>>(() =>
      apiClient.DELETE('/api/v5/watchlists/{group_id}/', {
        params: { path: { group_id: groupId(id) } },
      }),
    )
  },

  /**
   * The picker's rows for a typed query — the whole listed universe, not a
   * slice of it.
   *
   * `collection`'s `candidates` are the 200 most-traded of `/market/live/`'s
   * rows (`POPULAR_CANDIDATES`), and that endpoint took a `limit` capped at
   * 200 with no paging at all (`services/market/live.py` `MAX_LIMIT` was 200,
   * now 500) against a universe mm-be's own comment puts at "~300 rows"
   * (`services/market/fiftytwo.py:17`). So filtering those rows in memory is
   * why "Add symbols" answered **"No companies found" for companies that
   * exist**: every name outside the 200 most-traded was unfindable, and the
   * member was told it did not exist — the worst shape of wrong number there
   * is, because it reads as a fact about the market rather than about a fetch.
   *
   * `/market/search/` matches symbol OR name across every active company in
   * one bounded, relevance-ranked query
   * (`services/market/repository.py:498-521`), so a quiet name is findable.
   * It carries no price, which costs the picker nothing: it draws a symbol and
   * a name.
   *
   * An empty term returns nothing rather than asking — `q` is `min_length=1`
   * server-side, so an empty search is a 400. The screen shows "Popular now"
   * in that state, which is what it is.
   */
  searchCandidates: async (term: string, client?: QueryClient): Promise<StockCandidate[]> => {
    const q = term.trim().slice(0, SEARCH_MAX_LENGTH)
    if (q === '') return []
    const results = await marketEndpoints(client).search(q, SEARCH_LIMIT)
    return stockCandidateSchema
      .array()
      .parse(
        results.rows
          .filter((row) => !INDEX_TYPES.has(row.type))
          .map((row) => ({ symbol: row.key, name: row.name })),
      )
  },

  popular: async (client?: QueryClient): Promise<PopularStocks> => {
    const movers = await marketEndpoints(client).movers({ type: 'turnover', limit: 5 })
    return popularStocksSchema.parse({
      caption: 'Most active by turnover',
      // `MoverRow` carries no `logo_url` either — same CDN logo from the symbol.
      rows: movers.rows.map((row) => ({
        symbol: row.key,
        name: row.name,
        ltp: row.ltp,
        percentChange: row.change_percent,
        sparkline: sparklineValues(row.sparkline),
        logo: null,
      })),
    })
  },
}
